import { ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import React, { FC, useEffect, useState } from 'react';
import { UIProduct } from '../types';
import { Variant } from 'shared/types/product';
import { useFormat } from 'helpers/hooks/useFormat';
import Image from 'frontastic/lib/image';
import Link from 'components/commercetools-ui/atoms/link';
import Button from 'components/commercetools-ui/atoms/button';
import {
  BOWL_POSITION,
  CAPACITY_SIZE,
  CAPACITY_TO_OVERFLOW,
  CARTRIDGE_SIZE,
  CARTRIDGE_TYPE,
  CLEAN_FLUSH,
  COATING_METHOD,
  COLOR,
  GENERIC_ATTRIBUTES,
  GENERIC_MEDIA,
  GENERIC_PRODUCT,
  INDEPENDENT_LIVING_COMPLAINT,
  INLET_TYPE,
  ISAMBULANT,
  ISCOMPLIANT,
  ISNZCOMPLAINT,
  ITEM_CODE,
  ITEM_MATERIAL,
  ITEM_NAME,
  ITEM_TAPHOLES,
  LITRES_PER_MINUTE,
  MOUNT_TYPE,
  OUTLET_LENGTH,
  OUTLET_TYPE,
  OVERFLOW,
  PAN_HEIGHT,
  PAN_PROJECTION,
  PRODUCT_CODE,
  PRODUCT_TYPE,
  RAIL_LENGTH,
  RANGE,
  BRAND,
  SHAPE,
  SHOWER_HEAD_SIZE,
  SPRAY_FUNCTIONS,
  SURFACE_FINISH,
  TAPHOLES,
  TRAP_TYPE,
  WASTE_POSITION,
  WELS_FLOWRATE_1,
  WELS_FLOWRATE_2,
} from 'helpers/constants/attributes';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useParams } from 'next/navigation';
import { getPdpUrl } from 'helpers/utils/pdpUrlHelper';
type productDocumentProps = { value: string; name: string };
type ProductSpecificationProps = {
  product: UIProduct;
  variant: Variant;
  setSpinner: any;
};

interface MediaProps {
  name: string;
  value: string | string[]; // Assuming value can be a string or an array of strings
}

const ProductSpecification: FC<ProductSpecificationProps> = ({ product, variant, setSpinner }) => {
  const productCategory = product?.categories?.[0]?._url?.split('/').filter(Boolean)[0] ?? '';
  const productSubCategory = product?.categories?.[0].categoryId ?? '';
  const getKeyWelsFowRateUpdatedKey = (welsKeyNumber:number)=>{
    if(variant && variant.attributes?.[WELS_FLOWRATE_1] && variant.attributes?.[WELS_FLOWRATE_2]){
      return `Wels With flow Rate #${welsKeyNumber}`;
    }
    else{
      return 'Wels With flow Rate';
    }
  };

  const { formatMessage } = useFormat({ name: 'product' });
  const [errorMessage, setErrorMessage] = useState('')
  const getFilenameFromUrl = (url: string) => {
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  // Add new function for button click (BPIR) which will redirect to BPIR website.
  const bpirLink = async (itemCode:any) => {
    const url = `https://bpir.gwagroup.co.nz/Compliance/?code=${itemCode}`;
    window.open(url, '_blank');
  };

  const removeDuplicates = (array: string[]): string[] => {
    return Array.from(new Set(array));
  };

  const downloadAllFiles = async () => {
    let errorOccurred = false; // Flag to track if an error occurred

    try {
        setSpinner(true);
        // Create a new ZIP archive
        const zip = new JSZip();

        for (const file of variant?.attributes?.[GENERIC_MEDIA]) {
            const urls = [];

            if (file && file.value) {
                if (Array.isArray(file.value)) {
                    file.value.forEach((url: any) => urls.push(url));
                } else if (typeof file.value === 'string') {
                    urls.push(file.value);
                }
            }

            // Fetch and add each URL to the zip file
            for (const [index, url] of urls.entries()) {
                try {
                    // Append a unique query parameter to avoid caching issues
                    const uniqueUrl = `${url.replaceAll('ProductThumbnail', 'Original')}?cacheBust=${Date.now()}_${index}`;
                    
                    const response = await fetch(uniqueUrl);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch file from URL: ${url}`);
                    }
                    const content = await response.blob();
                    const filename = getFilenameFromUrl(url);
                    zip.file(filename, content);
                } catch (error) {
                    console.error('Error downloading file:', error);
                    setErrorMessage('Error downloading file, please try again later');
                    errorOccurred = true; // Set the error flag
                  setSpinner(false);
                  
                  setTimeout(() => {
                    setErrorMessage('');
                  }, 2000);
                  
                return;
                }
            }
      }
      // Only generate the ZIP file if no error occurred
        if (!errorOccurred) {
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            saveAs(zipBlob, 'files.zip');
        }

    } catch (error) {
        console.error('Error creating ZIP file:', error);
        setErrorMessage('Error creating ZIP file, please try again later');
    } finally {
        setSpinner(false); // Ensure loading is set to false when all operations are complete
    }
};


  
  // Count valid download links from props (no browser APIs) and seed synchronously so
  // the Technical Downloads section renders in the SSR HTML. Also fixes the previous
  // stale-closure counting (it only ever reached 1).
  const computeValidDownloadLinks = (): number => {
    const media = variant?.attributes?.[GENERIC_MEDIA];
    if (!Array.isArray(media)) return 0;
    return media.filter(
      (m: { value: string }) =>
        m?.value?.length && !(m.value === '-' || m.value === 'NULL' || m.value === 'warrantyURL'),
    ).length;
  };
  const [validDownloadLinks, setValidDownloadLinks] = useState<number>(() => computeValidDownloadLinks());
  useEffect(() => {
    setValidDownloadLinks(computeValidDownloadLinks());
  }, [variant]);
  const FILE_DOWNLOADS = [
    {
      name: 'RFA-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
          fill="none"
          className='print:w-[40%]'

        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36592)" />
          <defs>
            <pattern id="pattern0_12104_36592" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36592" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36592"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAxFJREFUeNrsWtFR4zAQNQwFuAN8FZzzkW/iCuAqIK6ApII4FSRUEKiAXAUx3/nAHeASTAXcLrO+0QhrJVuyFBh2ZieDLdn79q12VzJR9COnJWe6AcfjsXDwngq0nE6nTUgg7w7f9wC6HAPQuecImIMewDlpKCB1j2fi2D1p17x0DDCmobUGvQVNFMNK0HtcCxA2tTR/AT8r0Fiag+GVwfjKd2itmXsJGLSXQaDAtS0a3DEnJmZuvAIBgx6YEEvAoDkzt1I4AsE8cXPHWuwcKyuNIwoKpy7Z2YLpBcSGFSH9RgyYwhcjVqyAvGmeuwIwOy9ALFm51oRejrVmCJihBbE3K2ScqnY0gpP+gwGNndcR8ph4/ZWpKznVlpSUq0EfaweenwvPRlY3xHxm0tLYtCgcK+h9BPpEDHEgGvlZxExG8w4mzAwGolkrfWSpKKSVAGYzdtO4tu2GySG6QjofFYgDVkwWdTVm1nLJipPW3hqIAStteJTMmLa1nwcDYtIZg24BcKYZF1ObsgkGRMMKGrgQqvcfpnlEWQCYF9Ak1FaX8/Zdu6Bx3wI/E80ixlBDMDPvQExZobE16ETTDX9svNAJIQ4fjFgRAOXUznChduMdSB9WOtoRq737GMdBeR9WpHZkfzJAwKiSqRmdrNC8BhQz2vJUGOm9VhSnLk1wIENZkeb/0nQDXhixYkUItcw01M41u8NFKFb6dr9nGhBt31MPbNcTZnfYGBj5SO89gGPYbfmFprKaGDRU8Pm6FuTZ1IEXDmP/mnqkkgyQ5VLY6W2FM6676PMBt5dzrYK62NZTFV2b0LVnuq/qo/DL1ZJa+2Jo3XCZtX6DXpFG1Klynp1JjOLhdeLqEMMGyCMZda9YV12Cnx5KYa+ROtouWwHZUJu9E9KtnLFWgiZCGMVkfIP1hFixahq59FtE3cefpeDNWEqjKf1d071UkW5nUp1oFJmx3etbpV9drKvSaKxI1ap0m4Ze7F9L8BDb0T8NDH3/zORb/7dh5NsAMV3st0DvVSAbY1dA1ifg8L/Rj3wx+SfAAB1PSfhKvw1eAAAAAElFTkSuQmCC"
            />
          </defs>
        </svg>
      ),
    },
    {
      name: '3DS-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
                                    className='print:w-[40%]'
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36598)" />
          <defs>
            <pattern id="pattern0_12104_36598" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36598" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36598"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAABAZJREFUeNrsWctx4kAQFS7dV5sBjgA4+WgRAZABisBWBJgIgAjAEdhEgDj6ZDkCyxnIEex2bz1R7akWEqMP7K67qksWRqN505/X3TjOt1yWdMp86eXl5aGGd8Wk0c3NTXpOIL9qfOeGNKwb0ClAhvTyyPZFtIZHl3vSO9IE69UG5qotH+ZNk7KLDkm7pO8Erl/X+m4bILBhD7ce3Iuts6P/sWXiiwFCG+JT9kl7pH2cerfgMa8uMG4NAKbwe1s3YTCvtE5AYDatA4G7rI8ASJFyWT8LlmMrrmlNxxaMW8EKa+VfnI1W4Iv4xDXXVcC4lrGggWBuWCrf7QoX6isW440z8IBBAEyP7sOmLXKnfHbwb2x+RjoWmarocDLmZ2u+kS6YdxhckzziG/dLAYJP/JV0WhaEkCzmRmxdXgPu1phFzOBeCebeHQGQQDU30w4qgJuxhSdFVYBrkam+FIL0gmxzeVaIED+xst4YFpgqYB5RBTwJrknrci1P2WQmt0r65dg5kB2fLjaflS3PiIPrLPBlLKK2y0qaRZO11kcOyBRFYRY7Pvz9XbMarDrEcwcXRsDHKGnGTQKJc5LASljhHu4xVaz4pahEkGvx+FmUPJqqfmcE4IktwdxC+pM+G8DVkiMV8sa0yiVUv+wKYwKTCsvt0W1GR3qbSLiRdwlAZOz4hvvNAHCFHkXKW1E8NO1asQVABrQDB7XaIZr+LfuNbYnnlwpg30itvcaBKIHaK9ikabEViC5WKua8Eqgx13rWXoqenDPTPOe5LnhkIbIRZ6mBSNVmdRA3CWRrkJZvWO0BTB0iA6VGBopgvWtm9azsQJwsbOPOtbSIrEqfzJ4bLriElh0VmQVncoxzKlsEJ7hReu71qeMdBgB3elfI77GNnn2uNE5T9BAZAe5lF2iU7z9wzQvsWOGX+oGwyWnDIXzayyFA3/KQYhSQ7RAi6qKBkcWqEioH/8BmlOqW8OOHAl6ZIFh9uMttASfEIpPtwSPRKYGtSafEiGYqcv6H075EOJAZge3YWqRrBPPFinvGd5sZrW8xebEO9hQTjQ7Y+xp/D0Tnx+TYMdXoy0M0WwFSeYB7s91tzCIemPzQsnIaZlan61YGN5LETA7w6LMJyG8jrMDJgYcSiWM/CK/Uj8yhCRj99sh3R0b1vAPQFLVbgBHpJK+nrxvIEuYPcdp/RqOos1ZK7xKBZ+ZijiULzxEGE6+i/NnaAClKvzuDC1JsOFKy2wzXMKdqHYkhXoR1uPSPxLCb33VnBH3WFlRKv2p7Knxfk0WJdQ4lDOKtslw5/4gUWSSAyXdHXKZpScqQsVuiR0+y3y+q/M5eRcq433/jWl+CGE3TOaRbF5D5mQ9873zLXya/BRgAlBrILT3I1R4AAAAASUVORK5CYII="
            />
          </defs>
        </svg>
      ),
    },
    {
      name: 'DAE-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
                                    className='print:w-[40%]'
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36603)" />
          <defs>
            <pattern id="pattern0_12104_36603" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36603" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36603"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA+NJREFUeNrsmc9LG0EUxzebH11jpCvEohA1hVJqT7kJQbD2XKiFIj0UGj0r2ksvFpKA97bgWSP2IKHQ9C9IBC/eIhTsSbYQMGDRRWLMLzf9TjoL0yWJ0Ww2u5iBx/6anZnPvPfmvZ3luF4xV7FdV2F/fz+iQz9pSGpyclLuJkhVx/5ikPedAGoVJIrOb60ZtCHiMAv5BJEg82gvrScIb4T9Eg1AiDZm6K0k4AKWA2GA0hRG0huGN3p1of7BwsxaEkQDk4J8B0zIkiCM37yiK9lmuzBdA2GA5hmYiGVBGJgoJAyYzdu04ejEwFZXP3orlYrP4/EIo6NjT8g9p9P++PT0NL60tJRuABMBhEQ1o8IZA7K2tjbr842JGOQjt9vtcTgcwzzPDwuCwAHiv7r5fF7w+/3BRCKxd3BwsB4Oh7N1YGIEQoW5SRbQFsjExMTz/v7+oNPprGDghWs7czi4kZGRKVEUp+Lx+LfDw8MYgHINYEgWEMD5TCswbfkIZl8ZHBzMuVwu5ezszANzaqm9vr4+bnx8/HUwGNzZ3t5+G41GBS0MXZ79NNaIhjg7QEoE6PLy0iXLspt9dnV1xQNSOD8/v4ejm5yrx3K5/MDr9S6i2tMmWYCfasc4Zx8YGCgoisJnMpn78JOy3W5XIBwgC/ARDn7UyPyURikNtBGlIPOGrlrE3OA3RTJ4nZpsKUs2RRyxbK7VA+mB9ECMLx1JGhHh7SS3YoIiiRP2UqkkIHgWTAeSy+VcZMDVapW32WwK1bCCa1ud4FcCkAPR34PcrIC8q2Ia0zo6OvpwcnLyFUEwTwYOqR1JElmvPqJ8BXlWDqA1IJIFmAKEZK5zc3Pru7u7b7LZbBom1dJ7RCMECLmWiwDp4au6+Aj9tljZ2NiY8vl8izTRq5VischdXFxwmH1wVn5Sk/yFQ0GSpAzu/SHKNZWzLyws7CElTw8NDb2ADxSOj4/JF58MUMlyqxb9UNoxVRxBCr1i+ThCIdQPmmmdfi/ctKT0MC328/IZFc6sMHc2Ran7n4RuDhBTDNNbEuo9ZJ6TTbdQszZovapRIGqHxMyS9DJB93DJBttLsoXD/ds1rPkZnn2m1yFNGwHNpoLYjkb0MC32t4BMtUH2pmq7H+S3Aa5JHIlp3pMooCpb3YgjMuOEXxjzCjC7Hex/EK6OVvyMKbZdbE3MJ1Kno1iDmSMAy/RcBWHf3aJAyxSw0ewntT5JJywJrdr01EhIa+stDIars3SH9F617sTyG1PV2sSkOl0kNpO+FQhdaSS6M/4b16luzDRd/XqmpS3vMDPTXRqjqBdI1AQT/oPrFYuVvwIMALy8tBM68C9KAAAAAElFTkSuQmCC"
            />
          </defs>
        </svg>
      ),
    },
    {
      name: 'OBJ-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
                                    className='print:w-[40%]'
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36603)" />
          <defs>
            <pattern id="pattern0_12104_36603" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36603" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36603"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA+NJREFUeNrsmc9LG0EUxzebH11jpCvEohA1hVJqT7kJQbD2XKiFIj0UGj0r2ksvFpKA97bgWSP2IKHQ9C9IBC/eIhTsSbYQMGDRRWLMLzf9TjoL0yWJ0Ww2u5iBx/6anZnPvPfmvZ3luF4xV7FdV2F/fz+iQz9pSGpyclLuJkhVx/5ikPedAGoVJIrOb60ZtCHiMAv5BJEg82gvrScIb4T9Eg1AiDZm6K0k4AKWA2GA0hRG0huGN3p1of7BwsxaEkQDk4J8B0zIkiCM37yiK9lmuzBdA2GA5hmYiGVBGJgoJAyYzdu04ejEwFZXP3orlYrP4/EIo6NjT8g9p9P++PT0NL60tJRuABMBhEQ1o8IZA7K2tjbr842JGOQjt9vtcTgcwzzPDwuCwAHiv7r5fF7w+/3BRCKxd3BwsB4Oh7N1YGIEQoW5SRbQFsjExMTz/v7+oNPprGDghWs7czi4kZGRKVEUp+Lx+LfDw8MYgHINYEgWEMD5TCswbfkIZl8ZHBzMuVwu5ezszANzaqm9vr4+bnx8/HUwGNzZ3t5+G41GBS0MXZ79NNaIhjg7QEoE6PLy0iXLspt9dnV1xQNSOD8/v4ejm5yrx3K5/MDr9S6i2tMmWYCfasc4Zx8YGCgoisJnMpn78JOy3W5XIBwgC/ARDn7UyPyURikNtBGlIPOGrlrE3OA3RTJ4nZpsKUs2RRyxbK7VA+mB9ECMLx1JGhHh7SS3YoIiiRP2UqkkIHgWTAeSy+VcZMDVapW32WwK1bCCa1ud4FcCkAPR34PcrIC8q2Ia0zo6OvpwcnLyFUEwTwYOqR1JElmvPqJ8BXlWDqA1IJIFmAKEZK5zc3Pru7u7b7LZbBom1dJ7RCMECLmWiwDp4au6+Aj9tljZ2NiY8vl8izTRq5VischdXFxwmH1wVn5Sk/yFQ0GSpAzu/SHKNZWzLyws7CElTw8NDb2ADxSOj4/JF58MUMlyqxb9UNoxVRxBCr1i+ThCIdQPmmmdfi/ctKT0MC328/IZFc6sMHc2Ran7n4RuDhBTDNNbEuo9ZJ6TTbdQszZovapRIGqHxMyS9DJB93DJBttLsoXD/ds1rPkZnn2m1yFNGwHNpoLYjkb0MC32t4BMtUH2pmq7H+S3Aa5JHIlp3pMooCpb3YgjMuOEXxjzCjC7Hex/EK6OVvyMKbZdbE3MJ1Kno1iDmSMAy/RcBWHf3aJAyxSw0ewntT5JJywJrdr01EhIa+stDIars3SH9F617sTyG1PV2sSkOl0kNpO+FQhdaSS6M/4b16luzDRd/XqmpS3vMDPTXRqjqBdI1AQT/oPrFYuVvwIMALy8tBM68C9KAAAAAElFTkSuQmCC"
            />
          </defs>
        </svg>
      ),
    },
    {
      name: 'SKP-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
                                    className='print:w-[40%]'
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36608)" />
          <defs>
            <pattern id="pattern0_12104_36608" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36608" transform="scale(0.0166667)" />
            </pattern>
            <image
              id="image0_12104_36608"
              width="60"
              height="60"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADwAAAA8CAYAAAA6/NlyAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA8FJREFUeNrsmlFu2kAQhheLUkpSCUVtVVWVcG4AT3mFE5ScIOYEdU+QcAKTE+CcAHIC6CNPOCcIT1XVh4iqkeogSjsTjenWNWCbXbw4HmmFsY3x5/lnZr27jGWWWaosF+ak0WhUh4+BwP+dUHOgXZ6cnExUBcYbnAr67zK0Km13oLUBfKoacANuaijqz+G6OnxY0Jr0MBuyobUk4wmlDO0UNlvk7QE8hHJqgTlwm4MeA3Q11cA+aJ08XU0dMMoXWhcaerVM0DU6LAVaSxBWp0RokJQt8vRj8uKgjb0HBgjMymOuLKEZsN8MgO6KhNYSgEVP9qgO+83yZEzQx1SuhEFrO45XlLC54dRlaaKa3OCgrb0AJq/dQquH7IENuOzNQ5uY5JQGprgcr5DwKqvy3kRoaJi9bYr1rpLAdGNxZWj64xagWxx0L06vTJMcr9smGstfiznoZpyuqCYDlmKwLuByeK3/PEnQn+L0vzUJyclfX7c1Peh6AN3h+t+9nQMT7IBucJf97zaqKWw3VBMEa8TIxKJsyMlfPjDBdtmemAgPf2B7ZCKAb4J2/nRd9uP+ni0WC6WA87Iu7LozdjfFXuEde3lwyN68PkqNhwOtWCwst+e/5qmS9DBo57N8nj0vFJSLYWmSzgPw+3dv2Xw+Zw+zWfqBeXBsaZK086TK0rqZAixJguQ83QtJI+yXr98eJf3qqMwOSqU4l+nT+JZSZWmy7iAmrtlsHve6LRXr8ESSSFqiJ9ekDvG8KBa3+XlH5EylaGAcfbAFJheH3nPV7FpiUqFhl2OKOU/i9mGphKONHdd9mCQp5ahZWg8JPiVP2zgCwWVXB75fs3DLJtois3JkD9P4cDeO1wMSW3+TlOF3FzLzSm4DbN3nFRFrPPQ1iplEzPhXdH7o5RhROx5VJtd0Fm0Q8HPUkqjMCgDV35baVCenvhBA7xjQzmnXUmZw7DeXlC5oX5PyA87+51aEFY5GYmhZTMAQcBxgm7thk27Ey87fuVivBrwAnOIDIAh8KGYA4HI1AMqVyl0f9uP33rbAcSRd5+ZvO+QZXHp0TccrK2K9xiUVk22eJ0YrqyBplNUtQDuUNDyPOpznr4Jqrleb8TzYrrCAyTbfcge+WpwnBYy19JLzwEdoZxTX9pr31zHdfMPrmZFMjQ2lMHEPY6K5oVj+pzNBUm+u8IYnT5wNrNEkdyC0Sh0P1QxVNYzS8cjqcEApGbK/S31FLh9OZOAh0eXDIizqvT05SWcxvMHOSEIqWUUmsMEyyywzleyPAAMAXIGyZOQTkgkAAAAASUVORK5CYII="
            /> 
          </defs>
        </svg>
      ),
    },
    {
      name: 'DXF-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
                                    className='print:w-[40%]'
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36614)" />
          <defs>
            <pattern id="pattern0_12104_36614" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36614" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36614"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAypJREFUeNrsWe1x4jAQFUwKcAehg4Nf/IxdQUgFQAVABUAFkAogFcRXgZ2f/KOD+DrgKsjt3j0nOsayPpCFbw7NaIyNZenp7b5dSULcSrtKR/fC4XBYeejnSDUfDoenawL58NjfnuqiCUCmQNbUuTMz9I2ILiOqG6oF1Sl97+gTSDeE/TIDVJmNBI8yAtf32cedrw/RwHjGXzV+kqBmAJP4YsYnIxvN/8zAK/wjgYllmIB2AKHBxHTpGbwas0lJYHIGR88mbWFkafHuTPKbJyjZ7lIwXQ9sMBOxRZMJVKwUgqkEZnVNRpYObeZnqsZg1vwtArMLDgQz62IS4wqJZjamYGwXmpG5Y7telU8g1nyCkU2wMSDoZFbzysKWlTMwI8hz1DQj3JGqkz0NaItYUSvFNWASSLoRmG5DTv58dq2VYgWYowRm0wgQRGNVAMyltINn9mQqxQowaxNBcWWkzjfWcrJIl/RCwTg24iOw67iGjVwFzMbpQ8jvzMA3ZPMokFNZSXGjQJCOqDotaNCpKUDfrNgyMjHxjQpWUlcpbgrIYw0be03b5wtM1juQyHGQJlI8sklJLl3qvigC4ZgG8ahpe4KUxjWTxKa7DQFkCxOIKpaxdSWVcq93jXltGzctBLiFRRN28CdeCbIMG0rxKISPlAldYcjeoEKSG3F61xRlqkkpGEDljqKhFPeCAEEaklc4M+9IDgz2qnSsLEMxUrJSzngOFkw3D7xLcVeTksxrWGHzGPCagX4nuLcRjVQTr6zyr44GxEaa8Tfht9xrBltIvpgR+I5rHJGpjS33rnyUHvrMQ+yitKbYRnZeOCUKU5yIr/OPz+MD8edgJ8f/O6E4a8H+cRYKiNzxh2TL7Ox7evYNS9cMzwYSyJ3PgXsDcmbLPQD6efbsqIrWiEUdCdhKuG2/XgykdMI3aZ3+gPjw+wAHTKTi63Cn3xZGIgzgrxUhBjyGyizwXinfMzxb4P4e37jq0VvfYBY3mjZ94bbxfZPfPfwgw++XK4yvEGZHemogyJ0Ksme+/VGx8RakmKb0/11k582FhyuNMfIFZN2CCf8ubuUfK78EGADz81F09iyzbgAAAABJRU5ErkJggg=="
            />
          </defs>
        </svg>
      ),
    },
    {
      name: 'DWG-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
                                    className='print:w-[40%]'
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36603)" />
          <defs>
            <pattern id="pattern0_12104_36603" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36603" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36603"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA+NJREFUeNrsmc9LG0EUxzebH11jpCvEohA1hVJqT7kJQbD2XKiFIj0UGj0r2ksvFpKA97bgWSP2IKHQ9C9IBC/eIhTsSbYQMGDRRWLMLzf9TjoL0yWJ0Ww2u5iBx/6anZnPvPfmvZ3luF4xV7FdV2F/fz+iQz9pSGpyclLuJkhVx/5ikPedAGoVJIrOb60ZtCHiMAv5BJEg82gvrScIb4T9Eg1AiDZm6K0k4AKWA2GA0hRG0huGN3p1of7BwsxaEkQDk4J8B0zIkiCM37yiK9lmuzBdA2GA5hmYiGVBGJgoJAyYzdu04ejEwFZXP3orlYrP4/EIo6NjT8g9p9P++PT0NL60tJRuABMBhEQ1o8IZA7K2tjbr842JGOQjt9vtcTgcwzzPDwuCwAHiv7r5fF7w+/3BRCKxd3BwsB4Oh7N1YGIEQoW5SRbQFsjExMTz/v7+oNPprGDghWs7czi4kZGRKVEUp+Lx+LfDw8MYgHINYEgWEMD5TCswbfkIZl8ZHBzMuVwu5ezszANzaqm9vr4+bnx8/HUwGNzZ3t5+G41GBS0MXZ79NNaIhjg7QEoE6PLy0iXLspt9dnV1xQNSOD8/v4ejm5yrx3K5/MDr9S6i2tMmWYCfasc4Zx8YGCgoisJnMpn78JOy3W5XIBwgC/ARDn7UyPyURikNtBGlIPOGrlrE3OA3RTJ4nZpsKUs2RRyxbK7VA+mB9ECMLx1JGhHh7SS3YoIiiRP2UqkkIHgWTAeSy+VcZMDVapW32WwK1bCCa1ud4FcCkAPR34PcrIC8q2Ia0zo6OvpwcnLyFUEwTwYOqR1JElmvPqJ8BXlWDqA1IJIFmAKEZK5zc3Pru7u7b7LZbBom1dJ7RCMECLmWiwDp4au6+Aj9tljZ2NiY8vl8izTRq5VischdXFxwmH1wVn5Sk/yFQ0GSpAzu/SHKNZWzLyws7CElTw8NDb2ADxSOj4/JF58MUMlyqxb9UNoxVRxBCr1i+ThCIdQPmmmdfi/ctKT0MC328/IZFc6sMHc2Ran7n4RuDhBTDNNbEuo9ZJ6TTbdQszZovapRIGqHxMyS9DJB93DJBttLsoXD/ds1rPkZnn2m1yFNGwHNpoLYjkb0MC32t4BMtUH2pmq7H+S3Aa5JHIlp3pMooCpb3YgjMuOEXxjzCjC7Hex/EK6OVvyMKbZdbE3MJ1Kno1iDmSMAy/RcBWHf3aJAyxSw0ewntT5JJywJrdr01EhIa+stDIars3SH9F617sTyG1PV2sSkOl0kNpO+FQhdaSS6M/4b16luzDRd/XqmpS3vMDPTXRqjqBdI1AQT/oPrFYuVvwIMALy8tBM68C9KAAAAAElFTkSuQmCC"
            />
          </defs>
        </svg>
      ),
    },
    {
      name: 'technical-manual',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    }, 
    {
      name: 'technical-manual-set',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'technical-image',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'installation-guidelines-set',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M4.03826 2.47068L9.0018 6.28839V9.00193H6.28826L2.47055 4.03839L4.03826 2.47068ZM10.6685 9.4863C10.6685 9.46547 10.6685 9.43943 10.6685 9.41859V6.08526C10.6685 5.69464 10.4862 5.33005 10.1789 5.09568L4.76222 0.929011C4.26743 0.543595 3.5643 0.59047 3.11639 1.03318L1.03305 3.11651C0.590343 3.55922 0.543468 4.26234 0.923676 4.76234L5.09034 10.179C5.32472 10.4863 5.69451 10.6686 6.07993 10.6686H9.41326C9.43409 10.6686 9.46014 10.6686 9.48097 10.6686L14.7935 15.9759C13.7778 17.5384 13.9497 19.6478 15.3195 21.0176L21.1528 26.8509C21.8039 27.5019 22.8612 27.5019 23.5122 26.8509L26.8456 23.5176C27.4966 22.8665 27.4966 21.8092 26.8456 21.1582L21.0122 15.3248C19.6424 13.9551 17.5331 13.778 15.9706 14.7988L10.6685 9.4863ZM16.5018 16.5019C17.4237 15.5801 18.9133 15.5801 19.8351 16.5019L25.6685 22.3353L22.3351 25.6686L16.5018 19.8353C15.5799 18.9134 15.5799 17.4238 16.5018 16.5019ZM6.08514 22.7519C6.08514 22.5309 5.99734 22.319 5.84106 22.1627C5.68478 22.0064 5.47282 21.9186 5.2518 21.9186C5.03079 21.9186 4.81883 22.0064 4.66255 22.1627C4.50627 22.319 4.41847 22.5309 4.41847 22.7519C4.41847 22.9729 4.50627 23.1849 4.66255 23.3412C4.81883 23.4975 5.03079 23.5853 5.2518 23.5853C5.47282 23.5853 5.68478 23.4975 5.84106 23.3412C5.99734 23.1849 6.08514 22.9729 6.08514 22.7519ZM1.8768 20.2936C1.10076 21.0696 0.668468 22.1165 0.668468 23.2103C0.668468 25.4863 2.51743 27.3353 4.79347 27.3353C5.88722 27.3353 6.93409 26.903 7.71014 26.1269L13.0903 20.7467C12.7935 20.1634 12.606 19.5436 12.5226 18.9082L6.50701 24.9238C6.05389 25.3769 5.43409 25.6321 4.79347 25.6321C3.45493 25.6321 2.36638 24.5488 2.36638 23.2051C2.36638 22.5644 2.62159 21.9446 3.07472 21.4915L10.5174 14.054L9.3143 12.8509L1.8768 20.2936ZM25.6372 9.00193C25.6372 11.3353 24.4341 13.3821 22.6164 14.5644L23.8403 15.7884C25.9601 14.278 27.3403 11.7988 27.3403 9.00193C27.3403 7.71026 27.0435 6.4863 26.5226 5.39255C26.2935 4.91339 25.6633 4.84568 25.2883 5.22068L21.7518 8.75714C21.5956 8.91339 21.382 9.00193 21.1633 9.00193H19.8351C19.3768 9.00193 19.0018 8.62693 19.0018 8.16859V6.84568C19.0018 6.62693 19.0903 6.41339 19.2466 6.25714L22.7831 2.72068C23.1581 2.34568 23.0903 1.71547 22.6112 1.4863C21.5174 0.96547 20.2935 0.668595 19.0018 0.668595C16.0903 0.668595 13.5278 2.15818 12.0383 4.4238L13.3924 5.46547C14.5643 3.60089 16.6424 2.36651 19.0018 2.36651C19.5383 2.36651 20.0539 2.42901 20.5487 2.5488L18.0435 5.05401C17.5695 5.52797 17.3039 6.1738 17.3039 6.84568V8.16859C17.3039 9.56964 18.4393 10.6998 19.8351 10.6998H21.1581C21.8299 10.6998 22.4758 10.4342 22.9497 9.96026L25.4549 7.45505C25.5747 7.94984 25.6372 8.47068 25.6372 9.00193Z"
              fill="#666666"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'installation-guidelines',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M4.03826 2.47068L9.0018 6.28839V9.00193H6.28826L2.47055 4.03839L4.03826 2.47068ZM10.6685 9.4863C10.6685 9.46547 10.6685 9.43943 10.6685 9.41859V6.08526C10.6685 5.69464 10.4862 5.33005 10.1789 5.09568L4.76222 0.929011C4.26743 0.543595 3.5643 0.59047 3.11639 1.03318L1.03305 3.11651C0.590343 3.55922 0.543468 4.26234 0.923676 4.76234L5.09034 10.179C5.32472 10.4863 5.69451 10.6686 6.07993 10.6686H9.41326C9.43409 10.6686 9.46014 10.6686 9.48097 10.6686L14.7935 15.9759C13.7778 17.5384 13.9497 19.6478 15.3195 21.0176L21.1528 26.8509C21.8039 27.5019 22.8612 27.5019 23.5122 26.8509L26.8456 23.5176C27.4966 22.8665 27.4966 21.8092 26.8456 21.1582L21.0122 15.3248C19.6424 13.9551 17.5331 13.778 15.9706 14.7988L10.6685 9.4863ZM16.5018 16.5019C17.4237 15.5801 18.9133 15.5801 19.8351 16.5019L25.6685 22.3353L22.3351 25.6686L16.5018 19.8353C15.5799 18.9134 15.5799 17.4238 16.5018 16.5019ZM6.08514 22.7519C6.08514 22.5309 5.99734 22.319 5.84106 22.1627C5.68478 22.0064 5.47282 21.9186 5.2518 21.9186C5.03079 21.9186 4.81883 22.0064 4.66255 22.1627C4.50627 22.319 4.41847 22.5309 4.41847 22.7519C4.41847 22.9729 4.50627 23.1849 4.66255 23.3412C4.81883 23.4975 5.03079 23.5853 5.2518 23.5853C5.47282 23.5853 5.68478 23.4975 5.84106 23.3412C5.99734 23.1849 6.08514 22.9729 6.08514 22.7519ZM1.8768 20.2936C1.10076 21.0696 0.668468 22.1165 0.668468 23.2103C0.668468 25.4863 2.51743 27.3353 4.79347 27.3353C5.88722 27.3353 6.93409 26.903 7.71014 26.1269L13.0903 20.7467C12.7935 20.1634 12.606 19.5436 12.5226 18.9082L6.50701 24.9238C6.05389 25.3769 5.43409 25.6321 4.79347 25.6321C3.45493 25.6321 2.36638 24.5488 2.36638 23.2051C2.36638 22.5644 2.62159 21.9446 3.07472 21.4915L10.5174 14.054L9.3143 12.8509L1.8768 20.2936ZM25.6372 9.00193C25.6372 11.3353 24.4341 13.3821 22.6164 14.5644L23.8403 15.7884C25.9601 14.278 27.3403 11.7988 27.3403 9.00193C27.3403 7.71026 27.0435 6.4863 26.5226 5.39255C26.2935 4.91339 25.6633 4.84568 25.2883 5.22068L21.7518 8.75714C21.5956 8.91339 21.382 9.00193 21.1633 9.00193H19.8351C19.3768 9.00193 19.0018 8.62693 19.0018 8.16859V6.84568C19.0018 6.62693 19.0903 6.41339 19.2466 6.25714L22.7831 2.72068C23.1581 2.34568 23.0903 1.71547 22.6112 1.4863C21.5174 0.96547 20.2935 0.668595 19.0018 0.668595C16.0903 0.668595 13.5278 2.15818 12.0383 4.4238L13.3924 5.46547C14.5643 3.60089 16.6424 2.36651 19.0018 2.36651C19.5383 2.36651 20.0539 2.42901 20.5487 2.5488L18.0435 5.05401C17.5695 5.52797 17.3039 6.1738 17.3039 6.84568V8.16859C17.3039 9.56964 18.4393 10.6998 19.8351 10.6998H21.1581C21.8299 10.6998 22.4758 10.4342 22.9497 9.96026L25.4549 7.45505C25.5747 7.94984 25.6372 8.47068 25.6372 9.00193Z"
              fill="#666666"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'warranty-Info',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M4.03826 2.47068L9.0018 6.28839V9.00193H6.28826L2.47055 4.03839L4.03826 2.47068ZM10.6685 9.4863C10.6685 9.46547 10.6685 9.43943 10.6685 9.41859V6.08526C10.6685 5.69464 10.4862 5.33005 10.1789 5.09568L4.76222 0.929011C4.26743 0.543595 3.5643 0.59047 3.11639 1.03318L1.03305 3.11651C0.590343 3.55922 0.543468 4.26234 0.923676 4.76234L5.09034 10.179C5.32472 10.4863 5.69451 10.6686 6.07993 10.6686H9.41326C9.43409 10.6686 9.46014 10.6686 9.48097 10.6686L14.7935 15.9759C13.7778 17.5384 13.9497 19.6478 15.3195 21.0176L21.1528 26.8509C21.8039 27.5019 22.8612 27.5019 23.5122 26.8509L26.8456 23.5176C27.4966 22.8665 27.4966 21.8092 26.8456 21.1582L21.0122 15.3248C19.6424 13.9551 17.5331 13.778 15.9706 14.7988L10.6685 9.4863ZM16.5018 16.5019C17.4237 15.5801 18.9133 15.5801 19.8351 16.5019L25.6685 22.3353L22.3351 25.6686L16.5018 19.8353C15.5799 18.9134 15.5799 17.4238 16.5018 16.5019ZM6.08514 22.7519C6.08514 22.5309 5.99734 22.319 5.84106 22.1627C5.68478 22.0064 5.47282 21.9186 5.2518 21.9186C5.03079 21.9186 4.81883 22.0064 4.66255 22.1627C4.50627 22.319 4.41847 22.5309 4.41847 22.7519C4.41847 22.9729 4.50627 23.1849 4.66255 23.3412C4.81883 23.4975 5.03079 23.5853 5.2518 23.5853C5.47282 23.5853 5.68478 23.4975 5.84106 23.3412C5.99734 23.1849 6.08514 22.9729 6.08514 22.7519ZM1.8768 20.2936C1.10076 21.0696 0.668468 22.1165 0.668468 23.2103C0.668468 25.4863 2.51743 27.3353 4.79347 27.3353C5.88722 27.3353 6.93409 26.903 7.71014 26.1269L13.0903 20.7467C12.7935 20.1634 12.606 19.5436 12.5226 18.9082L6.50701 24.9238C6.05389 25.3769 5.43409 25.6321 4.79347 25.6321C3.45493 25.6321 2.36638 24.5488 2.36638 23.2051C2.36638 22.5644 2.62159 21.9446 3.07472 21.4915L10.5174 14.054L9.3143 12.8509L1.8768 20.2936ZM25.6372 9.00193C25.6372 11.3353 24.4341 13.3821 22.6164 14.5644L23.8403 15.7884C25.9601 14.278 27.3403 11.7988 27.3403 9.00193C27.3403 7.71026 27.0435 6.4863 26.5226 5.39255C26.2935 4.91339 25.6633 4.84568 25.2883 5.22068L21.7518 8.75714C21.5956 8.91339 21.382 9.00193 21.1633 9.00193H19.8351C19.3768 9.00193 19.0018 8.62693 19.0018 8.16859V6.84568C19.0018 6.62693 19.0903 6.41339 19.2466 6.25714L22.7831 2.72068C23.1581 2.34568 23.0903 1.71547 22.6112 1.4863C21.5174 0.96547 20.2935 0.668595 19.0018 0.668595C16.0903 0.668595 13.5278 2.15818 12.0383 4.4238L13.3924 5.46547C14.5643 3.60089 16.6424 2.36651 19.0018 2.36651C19.5383 2.36651 20.0539 2.42901 20.5487 2.5488L18.0435 5.05401C17.5695 5.52797 17.3039 6.1738 17.3039 6.84568V8.16859C17.3039 9.56964 18.4393 10.6998 19.8351 10.6998H21.1581C21.8299 10.6998 22.4758 10.4342 22.9497 9.96026L25.4549 7.45505C25.5747 7.94984 25.6372 8.47068 25.6372 9.00193Z"
              fill="#666666"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'specification-sheet',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    }, 
      {
      name: 'BOM-file',
      label: "Exploded Diagram",
      icon: (
        <>
          <svg className='ml-[-4px] print:ml-0 print:w-[40%]' width={50} height={50} viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.3572 6.67143H40.2717L59.3286 23.7569V64.4143H16.3572V6.67143Z" stroke="#BDBDBD" strokeWidth={2} />
            <rect x={13} y="46.2858" width="42.9714" height="13.4286" fill="#BDBDBD" />
            <path d="M24.225 51.9076C24.3126 51.804 24.3565 51.6805 24.3565 51.5371C24.3565 51.3937 24.3126 51.2662 24.225 51.1547C24.1453 51.0352 24.0258 50.9754 23.8665 50.9754H22.8625V52.063H23.8665C24.0258 52.063 24.1453 52.0112 24.225 51.9076ZM22.8625 54.0589V55.2301H23.974C24.1573 55.2301 24.2927 55.1704 24.3804 55.0508C24.476 54.9234 24.5238 54.7839 24.5238 54.6325V54.6206C24.5238 54.4692 24.472 54.3377 24.3684 54.2262C24.2728 54.1147 24.1413 54.0589 23.974 54.0589H22.8625ZM24.2848 57.2858H20.1974C20.1097 57.2858 20.034 57.2539 19.9703 57.1902C19.9066 57.1264 19.8747 57.0507 19.8747 56.9631V49.2425C19.8747 49.1548 19.9066 49.0791 19.9703 49.0154C20.034 48.9516 20.1097 48.9198 20.1974 48.9198H24.1652C25.2966 48.9198 26.1293 49.1269 26.6631 49.5412C27.2049 49.9476 27.4758 50.5651 27.4758 51.3937V51.4296C27.4758 51.7722 27.3762 52.0789 27.177 52.3498C26.9778 52.6128 26.7587 52.804 26.5197 52.9235C26.8384 53.051 27.1053 53.2781 27.3204 53.6047C27.5435 53.9234 27.6551 54.274 27.6551 54.6565C27.6551 56.4093 26.5316 57.2858 24.2848 57.2858ZM30.5934 49.6847C31.3265 49.0951 32.2945 48.8003 33.4976 48.8003C34.7007 48.8003 35.6648 49.099 36.3899 49.6966C37.1229 50.2862 37.4894 51.1188 37.4894 52.1945V54.0589C37.4894 55.1504 37.1269 55.9831 36.4018 56.5567C35.6847 57.1224 34.7167 57.4053 33.4976 57.4053C32.2786 57.4053 31.3065 57.1224 30.5815 56.5567C29.8644 55.9831 29.5059 55.1504 29.5059 54.0589V52.1945C29.5059 51.1109 29.8684 50.2743 30.5934 49.6847ZM32.8523 54.8357C33.0116 54.9951 33.2267 55.0748 33.4976 55.0748C33.7685 55.0748 33.9837 54.9951 34.143 54.8357C34.3024 54.6764 34.382 54.4334 34.382 54.1067V52.0988C34.382 51.7801 34.3024 51.5411 34.143 51.3818C33.9837 51.2144 33.7685 51.1308 33.4976 51.1308C33.2267 51.1308 33.0116 51.2144 32.8523 51.3818C32.6929 51.5411 32.6132 51.7801 32.6132 52.0988V54.1067C32.6132 54.4334 32.6929 54.6764 32.8523 54.8357ZM46.3432 48.9198H48.2315C48.3191 48.9198 48.3948 48.9516 48.4586 49.0154C48.5223 49.0791 48.5542 49.1548 48.5542 49.2425V56.9631C48.5542 57.0507 48.5223 57.1264 48.4586 57.1902C48.3948 57.2539 48.3191 57.2858 48.2315 57.2858H46.0683C45.9806 57.2858 45.905 57.2539 45.8412 57.1902C45.7775 57.1264 45.7456 57.0507 45.7456 56.9631V53.6884L44.9568 54.9791C44.8293 55.1783 44.6819 55.2779 44.5146 55.2779H43.678C43.5107 55.2779 43.3633 55.1783 43.2358 54.9791L42.447 53.6884V56.9631C42.447 57.0507 42.4151 57.1264 42.3514 57.1902C42.2877 57.2539 42.212 57.2858 42.1243 57.2858H39.9611C39.8735 57.2858 39.7978 57.2539 39.734 57.1902C39.6703 57.1264 39.6384 57.0507 39.6384 56.9631V49.2425C39.6384 49.1548 39.6703 49.0791 39.734 49.0154C39.7978 48.9516 39.8735 48.9198 39.9611 48.9198H41.8494C42.0725 48.9198 42.2438 49.0273 42.3633 49.2425L44.0963 52.1108L45.8293 49.2425C45.9488 49.0273 46.1201 48.9198 46.3432 48.9198Z" fill="#E4E4E4" />
            <path d="M43 30H29M43 30C44.1046 30 45 30.8954 45 32V38C45 39.1046 44.1046 40 43 40H29C27.8954 40 27 39.1046 27 38V32C27 30.8954 27.8954 30 29 30M43 30V28C43 26.8954 42.1046 26 41 26M29 30V28C29 26.8954 29.8954 26 31 26M31 26V24C31 22.8954 31.8954 22 33 22H39C40.1046 22 41 22.8954 41 24V26M31 26H41" stroke="#BDBDBD" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </>
      ),
    },
  ];
  //const itemAttrs = variant?.attributes?.['item-attributes'];
  const itemAttrs = variant?.attributes;
  const { locale } = useParams();
  const requiredItemAttributes = [
    'depth',
    'length',
    'width',
    'net-weight',
    'IsAS14281Compliant',
    'IsAS14281Ambulant',
    'IsIndependentLivingCompliant',
    'IsNZ41121Compliant',
  ];
  // Pure compute (no browser APIs) so the compliance rows render in the SSR HTML.
  const computeAddlAttr = () => {
    const p: any = [];
    requiredItemAttributes.forEach((ra: string) => {
      // const found = itemAttrs.filter((xx: any) => {
      //   return xx.name === ra;
      // });
      
      const found = Object.entries(itemAttrs || {}).filter(
        ([key, value]) => key === ra
      );
      // Checking attributes "IsAS14281Compliant", "IsAS14281Ambulant" & "IsNZ41121Compliant"
      // In PDP page, under Technical Information 
      // If locale is NZ then we will show IsNZ41121Compliant   and   if locale is AU then we will show "IsAS14281Compliant" & "IsAS14281Ambulant"
      if (found && found.length > 0) {
        let key = found[0][0];
        const value = found[0][1];
      
        // Rename based on original key
        if (key === 'IsAS14281Compliant') {
          key = 'AS1428.1 Accessible';
        } else if (key === 'IsAS14281Ambulant') {
          key = 'AS1428.1 Ambulant';
        } else if (key === 'IsIndependentLivingCompliant') {
          key = 'Independent Living Compliant';
        } else if (key === 'IsNZ41121Compliant') {
          key = 'NZS 4121 Compliant';
        }
      
        const result = { name: key, value }; // shape matches what you're likely pushing
      
        if (
          ra !== 'IsNZ41121Compliant' &&
          ra !== 'IsAS14281Compliant' &&
          ra !== 'IsAS14281Ambulant'
        ) {
          p.push(result);
        } else {
          if (locale === 'nz' && ra === 'IsNZ41121Compliant') {
            p.push(result);
          }
          if (
            locale === 'au' &&
            (ra === 'IsAS14281Compliant' || ra === 'IsAS14281Ambulant')
          ) {
            p.push(result);
          }
        }
      }
    });
    return p;
  };
  const [addlAttr, setAddlAttr] = useState<any[]>(() => computeAddlAttr());
  useEffect(() => {
    setAddlAttr(computeAddlAttr());
  }, [variant, locale]);
  return (
    <div
      id="specifier-info"
      className="relative left-full w-screen -translate-x-full bg-[#EDEDED] py-48 lg:grid lg:grid-cols-12 xl:py-80 2xl:py-120 "
    >
      <div className="px-24 md:px-28 lg:col-span-9 lg:col-start-4 lg:pl-30 lg:pr-48 xl:pl-0 xl:pr-60 2xl:pl-0 2xl:pr-80">
        <span className="xl:[2.25rem] block text-32 font-medium leading-10 text-primary-black xl:text-36 2xl:text-48 2xl:leading-[3.25rem]">
          {formatMessage({ id: 'technical.information', defaultMessage: 'Technical Information' })}
        </span>
        <div className="mt-32 flex flex-col lg:flex-row  ">
          <div className="flex-1 border-t border-[#ccc] py-32 lg:border-none">
            <span
              id="specifier-info-title"
              className="text-20 font-medium leading-6 text-primary-black xl:text-20 2xl:text-26"
            >
              {formatMessage({ id: 'specification', defaultMessage: 'Specifications' })}
            </span>
            <div className="mt-32 flex flex-col gap-16">
              {variant?.attributes &&
                [ITEM_CODE, PRODUCT_TYPE, RANGE, BRAND, COLOR, ITEM_MATERIAL, COATING_METHOD].map(
                  (attr: any, index: number) => {
                    const value = variant?.attributes?.[attr];
                    if(attr === PRODUCT_TYPE) {
                      return (
                        <div
                          key={index}
                          className="flex text-14 font-normal leading-6 print:justify-between print:text-xs lg:gap-0 lg:text-16"
                        >
                          <span className="flex-1 capitalize text-[#4D4D4D]">
                            {attr?.replaceAll('-', ' ').replace('gwa', '')}
                          </span>
                          <span className="ml-20 flex-1 text-[#090909] print:ml-0 print:text-right">
                           {
                           value?.join(", ")
                           }
                          </span>
                        </div>
                      );
                    }

                    if (
                      value !== 'Not Applicable' &&
                      value !== undefined &&
                      value !== '' &&
                      attr !== ITEM_NAME &&
                      !Array.isArray(value)
                    ) {
                      return (
                        <div
                          key={index}
                          className="flex text-14 font-normal leading-6 lg:gap-0 lg:text-16"
                        >
                          <span className="flex-1 capitalize text-[#4D4D4D]">
                            {attr?.replaceAll('-', ' ').replace('gwa', '')}
                          </span>
                          <span className="ml-20 flex-1 text-[#090909]">
                            {attr === RANGE && typeof value === 'string' ? (
                              <Link
                                link={`/${locale}/search?query=${encodeURIComponent(value)}&isPdp=true`}
                                className="hover:underline text-[#1259db]"
                              >
                                {value}
                              </Link>
                            ) : (
                              typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value
                            )}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  },
                )}
              {variant?.attributes &&
                [CAPACITY_SIZE, ITEM_TAPHOLES, TRAP_TYPE, INLET_TYPE, PAN_HEIGHT, PAN_PROJECTION, CLEAN_FLUSH, MOUNT_TYPE, OUTLET_TYPE, OUTLET_LENGTH, CARTRIDGE_SIZE, CARTRIDGE_TYPE, SHAPE, WASTE_POSITION, WELS_FLOWRATE_1, WELS_FLOWRATE_2, SPRAY_FUNCTIONS, RAIL_LENGTH, SHOWER_HEAD_SIZE, LITRES_PER_MINUTE, OVERFLOW, TAPHOLES, CAPACITY_TO_OVERFLOW, SURFACE_FINISH, BOWL_POSITION, 
                  ISCOMPLIANT, ISAMBULANT, INDEPENDENT_LIVING_COMPLAINT, ISNZCOMPLAINT].map((attr: any, index: number) => {
                    const value = variant?.attributes?.[attr];
                    if (
                      value !== 'Not Applicable' &&
                      value !== undefined &&
                      value !== '' &&
                      attr !== ITEM_NAME &&
                      !Array.isArray(value)
                    ) {
                      return (
                          <div key={index} className="flex text-14 font-normal leading-6 lg:gap-0 lg:text-16">
                            <span className="flex-1 capitalize text-[#4D4D4D]">
                              {attr === CARTRIDGE_SIZE ? 'Cartridge Size' :
                              attr === CLEAN_FLUSH ? 'Cleanflush' : 
                              productCategory != 'sanitaryware' && attr === WELS_FLOWRATE_1 ? getKeyWelsFowRateUpdatedKey(1) :
                              productCategory != 'sanitaryware' && attr === WELS_FLOWRATE_2 ? getKeyWelsFowRateUpdatedKey(2) :
                              attr.replaceAll('-', ' ').replace('gwa', '')}
                            </span>
                            <span className="ml-20 flex-1 text-[#090909]">
                              {(attr === PAN_HEIGHT || 
                                attr === PAN_PROJECTION || 
                                attr === RAIL_LENGTH || 
                                attr === OUTLET_LENGTH || 
                                attr === SHOWER_HEAD_SIZE) ? 
                                `${value} mm` : 
                                attr === CAPACITY_SIZE ? `${value} L` : 
                                attr === LITRES_PER_MINUTE ? `${value} L /min` :
                                attr === WELS_FLOWRATE_1 || attr === WELS_FLOWRATE_2
                                ? value.replace('L/min', '  L / min') :
                                attr === CARTRIDGE_SIZE ? value.replace(/(\d)([a-zA-Z])/g, '$1 $2') : 
                                typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
                                value}
                            </span>
                          </div>
                      )
                    }
                  }
              )}
              {addlAttr &&
                addlAttr.map((attr: any, index: number) => (
                  <>
                    {(
                      ((attr.name === ISCOMPLIANT || attr.name === ISNZCOMPLAINT || attr.name === INDEPENDENT_LIVING_COMPLAINT) && (
                        ['sanitaryware', 'tapware', 'showers', 'bathroom-accessories', 'basins',
                          'laundry-tubs'].includes(productCategory) ||
                        (productCategory == 'kitchen-sinks-accessories' && productSubCategory=='kitchen-accessories') ||
                        (productCategory == 'kitchen-sinks-accessories' && ['sinks', 'care-sinks'].includes(productSubCategory))
                      )) ||
                      (attr.name === ISAMBULANT && (
                        ['sanitaryware', 'bathroom-accessories'].includes(productCategory) ||
                        (productCategory == 'kitchen-sinks-accessories' && productSubCategory=='kitchen-accessories') 
                      ))
                    ) && (
                      <div
                        key={index}
                        className="flex text-14 font-normal leading-6 lg:gap-0 lg:text-16"
                      >
                        <span className="flex-1 capitalize text-[#4D4D4D]">
                          {attr.name.replaceAll('-', ' ').replace('gwa', '')}
                        </span>
                        <span className="ml-20 flex-1 text-[#090909]">
                          {typeof attr.value === 'boolean' ? (attr.value ? 'Yes' : 'No') : attr.value}
                        </span>
                      </div>
                    )}
                  </>
                ))}
            </div>
          </div>
          <div className="flex-1 border-t border-[#ccc] py-32 lg:border-none">
            <span className="text-20 font-medium leading-6 text-primary-black xl:text-20 2xl:text-26">
              {formatMessage({ id: 'product.codes', defaultMessage: 'Product Codes' })}
            </span>
            {product?.variants &&
              product?.variants?.map((variant) => (            
                <div className="mt-32 text-14 leading-6  2xl:text-16 gap-8"
                  key={variant.id}                >
                  <Link link={getPdpUrl(product?.slug, variant?.sku, variant?.attributes?.colour)} className="hover:underline">
                      <span className="font-semibold text-[#333]">
                        {typeof variant?.attributes?.[ITEM_NAME] === 'string'
                          ? variant?.attributes?.[ITEM_NAME]
                          : variant?.sku}
                      </span>
                      <br/>
                      <span className="font-normal text-[#666]">{variant?.attributes?.[ITEM_CODE]}</span>
                  </Link>
                </div>
              ))}
          </div>
        </div>
        {validDownloadLinks > 0 && (
          <div className="w-full print:grid print:grid-cols-2 print:gap-10 lg:flex">
            <div className="border-t border-[#ccc] py-32 print:border-none print:py-8 print:pr-0 lg:flex-1 lg:border-none lg:pr-48">
              <span className="text-20 font-medium leading-6 text-primary-black print:text-xl 2xl:text-26">
                {formatMessage({ id: 'technical.download', defaultMessage: 'Technical Downloads' })}
              </span>
              <div className="mt-32 grid grid-cols-2 gap-64 print:mt-2.5 print:grid-cols-3 print:gap-8 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3">
                {variant?.attributes?.[GENERIC_MEDIA] &&
                  variant?.attributes?.[GENERIC_MEDIA]?.sort(
                    (a: MediaProps, b: MediaProps) => a.name.length - b.name.length,
                  )?.map((media: MediaProps) => (
                    <>
                      {media?.value &&
                        FILE_DOWNLOADS.find((x) => {
                          if (media?.name === 'installation-guidelines') {
                            if (
                              variant?.attributes?.[GENERIC_MEDIA]?.find(
                                (it: any) => it.name === 'installation-guidelines-set',
                              )?.value?.length === 0
                            ) {
                              return x?.name === media?.name;
                            } else return false;
                          } else if (media?.name === 'technical-manual') {
                            if (
                              variant?.attributes?.[GENERIC_MEDIA]?.find(
                                (it: any) => it.name === 'technical-manual-set',
                              )?.value?.length === 0
                            ) {
                              return x?.name === media?.name;
                            } else return false;
                          } else return x?.name === media?.name;
                        }) &&
                        (typeof media.value === 'string' ? (
                          <Link
                            link={media.value.replace(/ProductThumbnail/g, 'Original')}
                            className="flex items-center"
                            target="_blank"
                            key={media.name}
                          >
                            {FILE_DOWNLOADS.find((x) => x?.name === media?.name) && (
                              <div className="flex items-center gap-8 print:gap-2">
                                {FILE_DOWNLOADS.find((x) => x?.name === media?.name)?.icon || (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                    width="50"
                                    height="50"
                                    viewBox="0 0 50 50"
                                    fill="none"
                                    className="print:w-[40%]"
                                  >
                                    <rect width="50" height="50" fill="url(#pattern0_12104_36603)" />
                                    <defs>
                                      <pattern
                                        id="pattern0_12104_36603"
                                        patternContentUnits="objectBoundingBox"
                                        width="1"
                                        height="1"
                                      >
                                        <use xlinkHref="#image0_12104_36603" transform="scale(0.02)" />
                                      </pattern>
                                      <image
                                        id="image0_12104_36603"
                                        width="50"
                                        height="50"
                                        xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA+NJREFUeNrsmc9LG0EUxzebH11jpCvEohA1hVJqT7kJQbD2XKiFIj0UGj0r2ksvFpKA97bgWSP2IKHQ9C9IBC/eIhTsSbYQMGDRRWLMLzf9TjoL0yWJ0Ww2u5iBx/6anZnPvPfmvZ3luF4xV7FdV2F/fz+iQz9pSGpyclLuJkhVx/5ikPedAGoVJIrOb60ZtCHiMAv5BJEg82gvrScIb4T9Eg1AiDZm6K0k4AKWA2GA0hRG0huGN3p1of7BwsxaEkQDk4J8B0zIkiCM37yiK9lmuzBdA2GA5hmYiGVBGJgoJAyYzdu04ejEwFZXP3orlYrP4/EIo6NjT8g9p9P++PT0NL60tJRuABMBhEQ1o8IZA7K2tjbr842JGOQjt9vtcTgcwzzPDwuCwAHiv7r5fF7w+/3BRCKxd3BwsB4Oh7N1YGIEQoW5SRbQFsjExMTz/v7+oNPprGDghWs7czi4kZGRKVEUp+Lx+LfDw8MYgHINYEgWEMD5TCswbfkIZl8ZHBzMuVwu5ezszANzaqm9vr4+bnx8/HUwGNzZ3t5+G41GBS0MXZ79NNaIhjg7QEoE6PLy0iXLspt9dnV1xQNSOD8/v4ejm5yrx3K5/MDr9S6i2tMmWYCfasc4Zx8YGCgoisJnMpn78JOy3W5XIBwgC/ARDn7UyPyURikNtBGlIPOGrlrE3OA3RTJ4nZpsKUs2RRyxbK7VA+mB9ECMLx1JGhHh7SS3YoIiiRP2UqkkIHgWTAeSy+VcZMDVapW32WwK1bCCa1ud4FcCkAPR34PcrIC8q2Ia0zo6OvpwcnLyFUEwTwYOqR1JElmvPqJ8BXlWDqA1IJIFmAKEZK5zc3Pru7u7b7LZbBom1dJ7RCMECLmWiwDp4au6+Aj9tljZ2NiY8vl8izTRq5VischdXFxwmH1wVn5Sk/yFQ0GSpAzu/SHKNZWzLyws7CElTw8NDb2ADxSOj4/JF58MUMlyqxb9UNoxVRxBCr1i+ThCIdQPmmmdfi/ctKT0MC328/IZFc6sMHc2Ran7n4RuDhBTDNNbEuo9ZJ6TTbdQszZovapRIGqHxMyS9DJB93DJBttLsoXD/ds1rPkZnn2m1yFNGwHNpoLYjkb0MC32t4BMtUH2pmq7H+S3Aa5JHIlp3pMooCpb3YgjMuOEXxjzCjC7Hex/EK6OVvyMKbZdbE3MJ1Kno1iDmSMAy/RcBWHf3aJAyxSw0ewntT5JJywJrdr01EhIa+stDIars3SH9F617sTyG1PV2sSkOl0kNpO+FQhdaSS6M/4b16luzDRd/XqmpS3vMDPTXRqjqBdI1AQT/oPrFYuVvwIMALy8tBM68C9KAAAAAElFTkSuQmCC"
                                      />
                                    </defs>
                                  </svg>
                                )}
                                <span className="text-14 font-semibold capitalize leading-5 text-primary-black print:text-xs 2xl:text-16">
                                  {media?.name === 'BOM-file'
                                    ? 'Exploded Diagram'
                                    : media?.name.replaceAll('-', ' ').replace('file', '').replace('set', '')}
                                </span>
                              </div>
                            )}
                          </Link>
                        ) : Array.isArray(media.value) ? (
                          (media.value as string[]).map((value, index) => (
                            <Link
                              link={value.replace(/ProductThumbnail/g, 'Original')}
                              className="flex items-center"
                              target="_blank"
                              key={`${media.name}-${index}`}
                            >
                              {FILE_DOWNLOADS.find((x) => x?.name === media?.name) && (
                                <div className="flex items-center justify-center gap-8 print:gap-2">
                                  {FILE_DOWNLOADS.find((x) => x?.name === media?.name)?.icon || (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      xmlnsXlink="http://www.w3.org/1999/xlink"
                                      width="50"
                                      height="50"
                                      viewBox="0 0 50 50"
                                      className="print:w-[40%]"
                                      fill="none"
                                    >
                                      <rect width="50" height="50" fill="url(#pattern0_12104_36603)" />
                                      <defs>
                                        <pattern
                                          id="pattern0_12104_36603"
                                          patternContentUnits="objectBoundingBox"
                                          width="1"
                                          height="1"
                                        >
                                          <use xlinkHref="#image0_12104_36603" transform="scale(0.02)" />
                                        </pattern>
                                        <image
                                          id="image0_12104_36603"
                                          width="50"
                                          height="50"
                                          xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA+NJREFUeNrsmc9LG0EUxzebH11jpCvEohA1hVJqT7kJQbD2XKiFIj0UGj0r2ksvFpKA97bgWSP2IKHQ9C9IBC/eIhTsSbYQMGDRRWLMLzf9TjoL0yWJ0Ww2u5iBx/6anZnPvPfmvZ3luF4xV7FdV2F/fz+iQz9pSGpyclLuJkhVx/5ikPedAGoVJIrOb60ZtCHiMAv5BJEg82gvrScIb4T9Eg1AiDZm6K0k4AKWA2GA0hRG0huGN3p1of7BwsxaEkQDk4J8B0zIkiCM37yiK9lmuzBdA2GA5hmYiGVBGJgoJAyYzdu04ejEwFZXP3orlYrP4/EIo6NjT8g9p9P++PT0NL60tJRuABMBhEQ1o8IZA7K2tjbr842JGOQjt9vtcTgcwzzPDwuCwAHiv7r5fF7w+/3BRCKxd3BwsB4Oh7N1YGIEQoW5SRbQFsjExMTz/v7+oNPprGDghWs7czi4kZGRKVEUp+Lx+LfDw8MYgHINYEgWEMD5TCswbfkIZl8ZHBzMuVwu5ezszANzaqm9vr4+bnx8/HUwGNzZ3t5+G41GBS0MXZ79NNaIhjg7QEoE6PLy0iXLspt9dnV1xQNSOD8/v4ejm5yrx3K5/MDr9S6i2tMmWYCfasc4Zx8YGCgoisJnMpn78JOy3W5XIBwgC/ARDn7UyPyURikNtBGlIPOGrlrE3OA3RTJ4nZpsKUs2RRyxbK7VA+mB9ECMLx1JGhHh7SS3YoIiiRP2UqkkIHgWTAeSy+VcZMDVapW32WwK1bCCa1ud4FcCkAPR34PcrIC8q2Ia0zo6OvpwcnLyFUEwTwYOqR1JElmvPqJ8BXlWDqA1IJIFmAKEZK5zc3Pru7u7b7LZbBom1dJ7RCMECLmWiwDp4au6+Aj9tljZ2NiY8vl8izTRq5VischdXFxwmH1wVn5Sk/yFQ0GSpAzu/SHKNZWzLyws7CElTw8NDb2ADxSOj4/JF58MUMlyqxb9UNoxVRxBCr1i+ThCIdQPmmmdfi/ctKT0MC328/IZFc6sMHc2Ran7n4RuDhBTDNNbEuo9ZJ6TTbdQszZovapRIGqHxMyS9DJB93DJBttLsoXD/ds1rPkZnn2m1yFNGwHNpoLYjkb0MC32t4BMtUH2pmq7H+S3Aa5JHIlp3pMooCpb3YgjMuOEXxjzCjC7Hex/EK6OVvyMKbZdbE3MJ1Kno1iDmSMAy/RcBWHf3aJAyxSw0ewntT5JJywJrdr01EhIa+stDIars3SH9F617sTyG1PV2sSkOl0kNpO+FQhdaSS6M/4b16luzDRd/XqmpS3vMDPTXRqjqBdI1AQT/oPrFYuVvwIMALy8tBM68C9KAAAAAElFTkSuQmCC"
                                        />
                                      </defs>
                                    </svg>
                                  )}
                                  <span className="text-14 font-semibold capitalize leading-5 text-primary-black print:text-xs 2xl:text-16">
                                    {media?.name.replaceAll('-', ' ').replace('file', '').replace('set', '')}
                                  </span>
                                </div>
                              )}
                            </Link>
                          ))
                        ) : null)}
                    </>
                  ))}
              </div>
               <Button
                onClick={downloadAllFiles}
                icon={<ArrowDownTrayIcon className="w-28 text-[#666]" />}
                iconPosition="middle"
                className="mt-48 flex w-full items-center justify-center rounded-none border-2 !border-[#666] !bg-[#F4F4F4] px-18 py-8 shadow-sm  hover:!bg-[#DCDCDC]  lg:w-fit"
              >
                <span className="text-16 font-semibold text-[#333]">
                  {formatMessage({ id: 'download.files', defaultMessage: 'Download all files' })}
                </span>
              </Button>
              {errorMessage &&
                <div className='text-red-500 font-medium text-16 pt-10'>{errorMessage}</div>
              }
              {/* Added new button named "Building Product Information Requirement" which will redirect to BPIR website when use click
              This button is only for Methven NZ and not for Methven AU */}
              {locale === 'nz' && variant?.attributes && (
                <Button
                  onClick={() => bpirLink(variant?.attributes?.[ITEM_CODE])}
                  icon={
                    <Image
                      src="/images/bpir.svg"
                      alt="Arrow Down"
                      width={28}
                      height={28}
                      className="w-28 text-[#666]"
                    />
                  }
                  iconPosition="middle"
                  className="mt-48 flex w-full items-center justify-center rounded-none border-2 !border-[#666] !bg-[#F4F4F4] px-18 py-8 shadow-sm hover:!bg-[#DCDCDC] lg:w-fit"
                >
                  <span className="text-16 font-semibold text-[#333]">
                    {formatMessage({ id: 'download.files', defaultMessage: 'Building Product Information Requirement' })}
                  </span>
                </Button>
              )}

              
            </div>
            <div className=" border-t border-[#ccc] pt-32 lg:flex-1 lg:border-none lg:pr-48">
              {variant?.attributes?.[GENERIC_MEDIA].filter(
                (att: { name: string }) => att?.name === 'technical-image',
              )[0].value.length > 0 && (
                <>
                  <span className="text-20 font-medium leading-6 text-primary-black lg:text-26">
                    {formatMessage({ id: 'measurements', defaultMessage: 'Measurements' })}
                  </span>
                  <div className="relative mt-32">
                    <Image
                      src={encodeURI(
                        variant?.attributes?.[GENERIC_MEDIA].filter(
                          (att: { name: string }) => att?.name === 'technical-image',
                        )[0]?.value,
                      )}
                      sizes="100vw"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'cover',
                      }}
                      width={500}
                      height={300}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}


        {/* Even if validDownloadLinks == 0 then also we will going to show button named "Building Product Information Requirement" which will redirect to BPIR website when use click
            This button is only for Methven NZ and not for Methven AU */}
        {validDownloadLinks == 0 && locale === 'nz' && variant?.attributes && (
            <Button
            onClick={() => bpirLink(variant?.attributes?.[ITEM_CODE])}
            icon={
              <Image
                src="/images/bpir.svg"
                alt="Arrow Down"
                width={28}
                height={28}
                className="w-28 text-[#666]"
              />
            }
            iconPosition="middle"
            className="mt-48 flex w-full items-center justify-center rounded-none border-2 !border-[#666] !bg-[#F4F4F4] px-18 py-8 shadow-sm hover:!bg-[#DCDCDC] lg:w-fit"
          >
            <span className="text-16 font-semibold text-[#333]">
              {formatMessage({ id: 'download.files', defaultMessage: 'Building Product Information Requirement' })}
            </span>
          </Button>
        )}


      </div>
    </div>
  );
};
export default ProductSpecification;
